import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, formatCurrency, formatDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Heart, AlertTriangle, Moon, XCircle, TrendingUp, CalendarPlus, Phone, Mail, Receipt, Clock, ChefHat, Users as UsersIcon, Wifi } from "lucide-react";

const HEALTH_META = {
  healthy:  { label: "Saludable",  color: "#10b981", bg: "bg-[#10b981]/10",  border: "border-[#10b981]/40",  Icon: Heart },
  at_risk:  { label: "En riesgo",  color: "#f59e0b", bg: "bg-[#f59e0b]/10",  border: "border-[#f59e0b]/40",  Icon: AlertTriangle },
  inactive: { label: "Inactivo",   color: "#7248ae", bg: "bg-[#7248ae]/10",  border: "border-[#7248ae]/40",  Icon: Moon },
  expired:  { label: "Vencido",    color: "#e11d48", bg: "bg-[#e11d48]/10",  border: "border-[#e11d48]/40",  Icon: XCircle },
};

export default function OwnerHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/owner/accounts-health"); setData(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Auto-refresh presence every 30s (silent reload of the same endpoint)
  useEffect(() => {
    const id = setInterval(async () => {
      try {
        const { data: fresh } = await api.get("/owner/accounts-health");
        setData(fresh);
      } catch (_) { /* silent */ }
    }, 30_000);
    return () => clearInterval(id);
  }, []);

  const extend = async (id, days) => {
    try {
      await api.post(`/owner/admins/${id}/extend`, { days });
      toast.success(`Suscripción +${days} días`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const pingTest = async (admin) => {
    try {
      const { data } = await api.get(`/owner/admins/${admin.id}/ping-test`);
      const { status, seconds_ago } = data;
      if (status === "online") {
        toast.success(`✅ ${admin.email} está en línea ahora (visto hace ${seconds_ago}s)`);
      } else if (status === "recent") {
        const mins = Math.round(seconds_ago / 60);
        toast.warning(`🟡 ${admin.email} estuvo activo hace ${mins} min — puede estar en pausa.`);
      } else if (status === "offline") {
        const mins = Math.round(seconds_ago / 60);
        const label = mins < 60 ? `${mins} min` : `${Math.round(mins / 60)} h`;
        toast.error(`🔴 ${admin.email} no responde — última actividad hace ${label}.`);
      } else {
        toast.error(`⚫ ${admin.email} nunca ha abierto la app desde este equipo.`);
      }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const presenceFrom = (lastSeenAt) => {
    if (!lastSeenAt) return { state: "never" };
    const ts = typeof lastSeenAt === "string" ? new Date(lastSeenAt).getTime() : lastSeenAt;
    if (Number.isNaN(ts)) return { state: "never" };
    const secs = Math.max(0, Math.round((Date.now() - ts) / 1000));
    if (secs <= 60) return { state: "online", secs };
    if (secs <= 300) return { state: "recent", secs };
    return { state: "offline", secs };
  };

  if (loading) return <p className="text-[#7248ae]">Cargando salud de cuentas…</p>;
  if (!data) return null;

  const admins = filter === "all" ? data.admins : data.admins.filter(a => a.health === filter);

  return (
    <div className="animate-fade-up" data-testid="owner-health-page">
      <p className="text-xs uppercase tracking-[0.3em] text-[#7248ae] mb-2">Retención</p>
      <h1 className="font-display text-4xl sm:text-5xl font-black text-[#1a0f2e] tracking-tight">Salud de cuentas</h1>
      <p className="text-[#7248ae] mt-2 mb-8 max-w-2xl">
        Vista 360° de cada administrador: uso de los últimos 30 días, ticket promedio, días al vencimiento y estado de salud.
        Las cuentas <strong>en riesgo</strong> y <strong>vencidas</strong> aparecen primero.
      </p>

      {/* KPI summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {Object.keys(HEALTH_META).map((key, i) => {
          const m = HEALTH_META[key];
          const Icon = m.Icon;
          const count = data.summary[key] || 0;
          const active = filter === key;
          return (
            <button
              key={key}
              onClick={() => setFilter(active ? "all" : key)}
              className={`p-5 rounded-2xl border text-left transition-all hover:-translate-y-1 animate-fade-up stagger-${i+1} ${active ? `${m.bg} ${m.border}` : "bg-white border-[#b182ec]/30"}`}
              data-testid={`health-filter-${key}`}
            >
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs uppercase tracking-wider" style={{ color: m.color }}>{m.label}</p>
                <Icon className="h-5 w-5" style={{ color: m.color }} />
              </div>
              <p className="font-display text-3xl font-black text-[#1a0f2e]">{count}</p>
              <p className="text-[10px] uppercase tracking-wider text-[#7248ae] mt-1">{count === 1 ? "cuenta" : "cuentas"}</p>
            </button>
          );
        })}
      </div>

      {filter !== "all" && (
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-[#7248ae]">Filtrando por <strong className="text-[#1a0f2e]">{HEALTH_META[filter].label}</strong></p>
          <Button size="sm" variant="ghost" onClick={() => setFilter("all")} className="text-[#3e166c]" data-testid="clear-filter-button">Ver todas</Button>
        </div>
      )}

      {/* Cards */}
      {admins.length === 0 ? (
        <Card className="p-12 text-center border-dashed border-[#b182ec]/40">
          <p className="text-[#7248ae]">No hay cuentas con ese estado.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="health-cards">
          {admins.map((a, idx) => {
            const m = HEALTH_META[a.health] || HEALTH_META.healthy;
            const Icon = m.Icon;
            const presence = presenceFrom(a.last_seen_at);
            return (
              <Card
                key={a.id}
                className={`p-6 border ${m.border} rounded-2xl card-glow transition-all hover:-translate-y-0.5 animate-fade-up stagger-${(idx%6)+1}`}
                data-testid={`health-card-${a.email}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-display text-2xl font-bold text-[#1a0f2e] truncate">{a.name}</h3>
                      {presence.state === "online" && (
                        <span
                          className="inline-flex items-center gap-1.5 px-2 h-5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-semibold uppercase tracking-wider"
                          title={`Activo hace ${presence.secs}s`}
                          data-testid={`presence-${a.email}`}
                        >
                          <span className="relative flex h-1.5 w-1.5">
                            <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70 animate-ping" />
                            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                          </span>
                          Activo ahora
                        </span>
                      )}
                      {presence.state === "recent" && (
                        <span
                          className="inline-flex items-center gap-1.5 px-2 h-5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-semibold uppercase tracking-wider"
                          title={`Visto hace ${Math.round(presence.secs/60)} min`}
                          data-testid={`presence-${a.email}`}
                        >
                          <span className="inline-flex h-1.5 w-1.5 rounded-full bg-amber-500" />
                          Hace {Math.round(presence.secs/60)} min
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-[#7248ae] truncate">{a.restaurant_name}</p>
                    <div className="flex flex-wrap gap-3 mt-2 text-xs text-[#7248ae]">
                      <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{a.email}</span>
                      {a.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{a.phone}</span>}
                    </div>
                  </div>
                  <Badge className="text-white" style={{ background: m.color }} data-testid={`health-badge-${a.email}`}>
                    <Icon className="h-3 w-3 mr-1" />{m.label}
                  </Badge>
                </div>

                <div className="grid grid-cols-3 gap-3 mb-4 text-center">
                  <div className={`p-3 rounded-xl ${m.bg}`}>
                    <p className="text-[10px] uppercase tracking-wider text-[#7248ae]">Ingresos 30d</p>
                    <p className="font-display text-lg font-bold text-[#1a0f2e]">{formatCurrency(a.revenue_30d)}</p>
                  </div>
                  <div className={`p-3 rounded-xl ${m.bg}`}>
                    <p className="text-[10px] uppercase tracking-wider text-[#7248ae]">Pedidos 30d</p>
                    <p className="font-display text-lg font-bold text-[#1a0f2e]">{a.orders_30d}</p>
                  </div>
                  <div className={`p-3 rounded-xl ${m.bg}`}>
                    <p className="text-[10px] uppercase tracking-wider text-[#7248ae]">Ticket prom.</p>
                    <p className="font-display text-lg font-bold text-[#1a0f2e]">{formatCurrency(a.avg_ticket)}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
                  <div className="flex items-center gap-2 text-[#1a0f2e]">
                    <Clock className="h-4 w-4 text-[#7248ae]" />
                    {a.subscription_active ? (
                      <span><strong style={{ color: a.subscription_days_left <= 5 ? "#f59e0b" : "#1a0f2e" }}>{a.subscription_days_left}</strong> días restantes</span>
                    ) : (
                      <span className="text-[#e11d48]"><strong>Vencido</strong></span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-[#1a0f2e]">
                    <Receipt className="h-4 w-4 text-[#7248ae]" />
                    {a.last_paid_at ? (
                      <span className="text-xs">Último: {formatDate(a.last_paid_at)}</span>
                    ) : (
                      <span className="text-xs text-[#7248ae]">Sin pedidos pagados</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-[#1a0f2e] text-xs">
                    <ChefHat className="h-4 w-4 text-[#7248ae]" />{a.dishes_count} platillos · {a.tables_count} mesas
                  </div>
                  <div className="flex items-center gap-2 text-[#1a0f2e] text-xs">
                    <UsersIcon className="h-4 w-4 text-[#7248ae]" />{a.waiters_count} meseros
                  </div>
                </div>

                <div className="pt-4 border-t border-[#b182ec]/20 flex items-center justify-between flex-wrap gap-2">
                  <div className="text-xs text-[#7248ae]">
                    Histórico: <strong className="text-[#1a0f2e]">{formatCurrency(a.total_revenue_lifetime)}</strong> en {a.total_orders_lifetime} pedidos
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button size="sm" variant="outline" onClick={() => pingTest(a)} className="border-[#b182ec]/50" data-testid={`ping-${a.email}`}>
                      <Wifi className="h-3 w-3 mr-1" />Probar conexión
                    </Button>
                    <Button size="sm" variant="outline" asChild className="border-[#b182ec]/50" data-testid={`contact-${a.email}`}>
                      <a href={`mailto:${a.email}?subject=oRdeN%20-%20Tu%20cuenta`}>Contactar</a>
                    </Button>
                    <Button size="sm" onClick={() => extend(a.id, 30)} className="bg-[#3e166c] hover:bg-[#7248ae] text-white" data-testid={`renew-${a.email}`}>
                      <CalendarPlus className="h-3 w-3 mr-1" />+30 días
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
